import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import {
  CHECKLIST_ACOES,
  defaultAcoesRecord,
  emptyGrupo,
  type ChecklistGrupoForm,
} from '@/constants/oficinaChecklistAcoes';

type Props = {
  grupos: ChecklistGrupoForm[];
  onChange: (grupos: ChecklistGrupoForm[]) => void;
};

export const ChecklistTemplateGruposEditor: React.FC<Props> = ({ grupos, onChange }) => {
  return (
    <div className="border-t pt-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">Grupos e itens</h4>
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...grupos, emptyGrupo()])}>
          <Plus className="w-4 h-4 mr-1" />
          Grupo
        </Button>
      </div>
      {grupos.map((grupo, gIdx) => (
        <div key={gIdx} className="border rounded-lg p-4 bg-muted/20 space-y-3">
          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <div className="flex-1">
              <label className="block text-xs text-muted-foreground mb-1">Grupo</label>
              <Input
                value={grupo.nome}
                onChange={(e) => {
                  const next = [...grupos];
                  next[gIdx] = { ...next[gIdx]!, nome: e.target.value };
                  onChange(next);
                }}
                placeholder="Ex: FREIOS"
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={grupos.length <= 1}
              onClick={() => onChange(grupos.filter((_, i) => i !== gIdx))}
              aria-label="Remover grupo"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          {grupo.itens.map((it, iIdx) => (
            <div key={iIdx} className="rounded-md border bg-background p-3 space-y-2">
              <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                <div className="flex-1">
                  <label className="block text-xs text-muted-foreground mb-1">Item</label>
                  <Input
                    value={it.titulo}
                    onChange={(e) => {
                      const next = [...grupos];
                      const itens = [...next[gIdx]!.itens];
                      itens[iIdx] = { ...itens[iIdx]!, titulo: e.target.value };
                      next[gIdx] = { ...next[gIdx]!, itens };
                      onChange(next);
                    }}
                    placeholder="Ex: Pastilha dianteira"
                  />
                </div>
                <div className="flex items-center gap-2 pb-0.5">
                  <Checkbox
                    id={`foto-${gIdx}-${iIdx}`}
                    checked={it.obrigatoriaFoto}
                    onChange={() => {
                      const next = [...grupos];
                      const itens = [...next[gIdx]!.itens];
                      itens[iIdx] = { ...itens[iIdx]!, obrigatoriaFoto: !itens[iIdx]!.obrigatoriaFoto };
                      next[gIdx] = { ...next[gIdx]!, itens };
                      onChange(next);
                    }}
                  />
                  <label htmlFor={`foto-${gIdx}-${iIdx}`} className="text-xs whitespace-nowrap">
                    Foto obrig.
                  </label>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={grupo.itens.length <= 1}
                  onClick={() => {
                    const next = [...grupos];
                    next[gIdx] = {
                      ...next[gIdx]!,
                      itens: next[gIdx]!.itens.filter((_, i) => i !== iIdx),
                    };
                    onChange(next);
                  }}
                  aria-label="Remover item"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-2">Ações permitidas</p>
                <div className="flex flex-wrap gap-3">
                  {CHECKLIST_ACOES.map((acao) => (
                    <label
                      key={acao.key}
                      className="inline-flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <Checkbox
                        checked={it.acoes[acao.key]}
                        onChange={() => {
                          const next = [...grupos];
                          const itens = [...next[gIdx]!.itens];
                          itens[iIdx] = {
                            ...itens[iIdx]!,
                            acoes: { ...itens[iIdx]!.acoes, [acao.key]: !itens[iIdx]!.acoes[acao.key] },
                          };
                          next[gIdx] = { ...next[gIdx]!, itens };
                          onChange(next);
                        }}
                      />
                      <span className="font-medium" style={{ color: acao.color }}>
                        {acao.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              const next = [...grupos];
              next[gIdx] = {
                ...next[gIdx]!,
                itens: [...next[gIdx]!.itens, { titulo: '', obrigatoriaFoto: false, acoes: defaultAcoesRecord(true) }],
              };
              onChange(next);
            }}
          >
            <Plus className="w-4 h-4 mr-1" />
            Item
          </Button>
        </div>
      ))}
    </div>
  );
};
